import React, { useState } from 'react';
import { Activity, Loader2, Twitter, Instagram, Linkedin } from 'lucide-react';
import { supabase } from '../services/supabase';
import { sendGAEvent } from '../lib/gtag';

interface PendingApprovalScreenProps {
  orgName: string;
  userId: string;
  onSignOut: () => void;
}

const PROGRAM_TYPES = ['School', 'Club', 'College', 'Other'];
const AGE_GROUPS    = ['Middle School / 14U', 'High School / 15U-18U', 'Collegiate / Adult'];
const STAT_TOOLS    = ['Pen & Paper', 'Hudl', 'SoloStats', 'Other'];
const ATTRIBUTION   = ['Facebook / Instagram', 'YouTube', 'Another coach', 'Google search', 'Other'];

interface PillGroupProps {
  options: string[];
  values: string[];
  onChange: (vals: string[]) => void;
}

function PillGroup({ options, values, onChange }: PillGroupProps) {
  const toggle = (opt: string) =>
    onChange(values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const selected = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={[
              'rounded-lg border px-3.5 py-2 text-sm font-medium transition-all active:scale-95',
              selected
                ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-[0_0_0_1px] shadow-blue-400/40'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700',
            ].join(' ')}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const PendingApprovalScreen: React.FC<PendingApprovalScreenProps> = ({ orgName, userId, onSignOut }) => {
  const [programType,      setProgramType]      = useState<string[]>([]);
  const [ageGroup,         setAgeGroup]         = useState<string[]>([]);
  const [statTool,         setStatTool]         = useState<string[]>([]);
  const [statToolOther,    setStatToolOther]    = useState('');
  const [attribution,      setAttribution]      = useState<string[]>([]);
  const [attributionOther, setAttributionOther] = useState('');
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [surveySubmitted,  setSurveySubmitted]  = useState(false);

  // All questions optional — submit always available
  const canSubmit =
    (!statTool.includes('Other') || statToolOther.trim() !== '') &&
    (!attribution.includes('Other') || attributionOther.trim() !== '') &&
    !isSubmitting;

  const handleSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    const resolveOther = (vals: string[], other: string) =>
      vals.map(v => v === 'Other' ? other.trim() : v).join(', ');

    const payload: Record<string, string> = {
      programType:  programType.join(', '),
      ageGroup:     ageGroup.join(', '),
      statTool:     resolveOther(statTool, statToolOther),
      attribution:  resolveOther(attribution, attributionOther),
      submittedAt:  new Date().toISOString(),
    };

    const { error: surveyError } = await supabase
      .from('profiles')
      .upsert({ id: userId, waitlist_survey: payload });
    if (surveyError) console.error('[survey] upsert failed:', surveyError.message);

    sendGAEvent('event', 'survey_completed');
    setIsSubmitting(false);
    setSurveySubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

        <div className="p-8 space-y-5">
          {/* Logo */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900">
              Sideout<span className="text-orange-500">IQ</span>
            </h1>
            <p className="text-xs font-semibold tracking-widest text-slate-400 uppercase mt-0.5">
              Command the Court
            </p>
          </div>

          {/* Pulsing icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center animate-pulse">
              <Activity className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          {/* Headline + body */}
          <div className="text-center space-y-2">
            <h2 className="font-bold text-slate-900 text-xl">You're on the Waitlist.</h2>
            <p className="text-slate-500 text-sm leading-relaxed">
              Your organization's profile for{' '}
              <strong className="text-slate-800">{orgName}</strong> has been created and added
              to our waitlist. We are manually reviewing applications for our limited closed
              beta and will notify you via email if a spot opens up for your program.
            </p>
          </div>

          {/* Survey */}
          <div className="border-t border-slate-100 pt-5">
            {surveySubmitted ? (
              <div className="text-center py-3 space-y-1">
                <p className="text-sm font-semibold text-emerald-600">Thanks for sharing! 🎉</p>
                <p className="text-xs text-slate-400">Your responses help us prioritize.</p>
              </div>
            ) : (
              <form onSubmit={handleSurveySubmit} className="space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Answer these quick questions to get priority beta access:
                </p>

                {/* Q1 — program type */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-slate-600">
                    What type of program do you coach?
                  </label>
                  <PillGroup options={PROGRAM_TYPES} values={programType} onChange={setProgramType} />
                </div>

                {/* Q2 — age group */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-slate-600">
                    What age group(s)?
                  </label>
                  <PillGroup options={AGE_GROUPS} values={ageGroup} onChange={setAgeGroup} />
                </div>

                {/* Q3 — stat tool */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-slate-600">
                    How do you currently track stats?
                  </label>
                  <PillGroup
                    options={STAT_TOOLS}
                    values={statTool}
                    onChange={vals => { setStatTool(vals); if (!vals.includes('Other')) setStatToolOther(''); }}
                  />
                  {statTool.includes('Other') && (
                    <input
                      type="text"
                      value={statToolOther}
                      onChange={e => setStatToolOther(e.target.value)}
                      placeholder="Please describe…"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder:text-slate-400"
                    />
                  )}
                </div>

                {/* Q4 — attribution */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-slate-600">
                    How did you hear about SideOutIQ?
                  </label>
                  <PillGroup
                    options={ATTRIBUTION}
                    values={attribution}
                    onChange={vals => { setAttribution(vals); if (!vals.includes('Other')) setAttributionOther(''); }}
                  />
                  {attribution.includes('Other') && (
                    <input
                      type="text"
                      value={attributionOther}
                      onChange={e => setAttributionOther(e.target.value)}
                      placeholder="Please describe…"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder:text-slate-400"
                    />
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full flex items-center justify-center gap-2 bg-orange-600 text-white text-sm font-bold py-2.5 rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
              </form>
            )}
          </div>

          {/* Refresh / Sign Out */}
          <div className="flex items-center justify-center gap-6 pt-1">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
            >
              Sign Out
            </button>
          </div>

          {/* Social links */}
          <div className="border-t border-slate-100 pt-5 text-center space-y-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Stay Updated
            </p>
            <div className="flex items-center justify-center gap-5">
              <a href="#" aria-label="Twitter / X" className="text-slate-400 hover:text-blue-500 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" aria-label="Instagram" className="text-slate-400 hover:text-pink-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" aria-label="LinkedIn" className="text-slate-400 hover:text-blue-700 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PendingApprovalScreen;
